import React from 'react';
import { DollarSign, Archive, TrendingUp, BarChart3, ShoppingCart } from 'lucide-react';
import { StatCard, MktBadge, Th, Td } from '../WMSShared';

export default function ReportsTab({ profitReport, products, lots, costingMethod, changeCostingMethod, cur = 'S$' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Profit & Costing Reports</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">Costing Method:</span>
          {['fifo', 'lifo', 'average'].map(m => (
            <button key={m} onClick={() => changeCostingMethod(m)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${costingMethod === m ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {m.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {profitReport?.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard label="Revenue" value={`${cur}${profitReport.summary.total_revenue?.toLocaleString()}`} icon={DollarSign} color="blue" sub={`${profitReport.summary.order_count} orders`} />
          <StatCard label="COGS" value={`${cur}${profitReport.summary.total_cogs?.toLocaleString()}`} icon={Archive} color="orange" sub={`Method: ${costingMethod.toUpperCase()}`} />
          <StatCard label="Gross Profit" value={`${cur}${profitReport.summary.total_profit?.toLocaleString()}`} icon={TrendingUp} color="emerald" />
          <StatCard label="Margin" value={`${profitReport.summary.overall_margin}%`} icon={BarChart3} color="purple" />
          <StatCard label="Avg per Order" value={`${cur}${profitReport.summary.order_count > 0 ? Math.round(profitReport.summary.total_profit / profitReport.summary.order_count) : 0}`} icon={ShoppingCart} color="pink" />
        </div>
      )}

      <div className="bg-white rounded-xl border shadow-sm mb-6">
        <div className="px-5 py-4 border-b"><h3 className="font-semibold">Order Profitability</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><Th>Order</Th><Th>Customer</Th><Th>Marketplace</Th><Th>Revenue</Th><Th>COGS</Th><Th>Profit</Th><Th>Margin</Th><Th>Method</Th></tr></thead>
            <tbody className="divide-y">
              {(profitReport?.orders || []).map(o => (
                <tr key={o.order_id} className="hover:bg-gray-50">
                  <Td className="font-mono text-xs">{o.order_id}</Td><Td>{o.customer_name}</Td>
                  <Td><MktBadge m={o.marketplace} /></Td>
                  <Td className="font-medium">{cur}{(o.grand_total || o.total || 0).toFixed(2)}</Td>
                  <Td className="text-orange-600">{cur}{(o.cogs || 0).toFixed(2)}</Td>
                  <Td className={`font-semibold ${(o.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{cur}{(o.profit || 0).toFixed(2)}</Td>
                  <Td><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${(o.margin || 0) >= 20 ? 'bg-emerald-100 text-emerald-700' : (o.margin || 0) >= 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{(o.margin || 0).toFixed(1)}%</span></Td>
                  <Td className="text-xs text-gray-400 uppercase">{o.costing_method || '-'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(!profitReport?.orders || profitReport.orders.length === 0) && <div className="p-8 text-center text-gray-400">No orders with COGS data yet. New orders will show profitability.</div>}
      </div>

      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-5 py-4 border-b"><h3 className="font-semibold">Active Stock Lots ({lots.length})</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><Th>Lot ID</Th><Th>Product</Th><Th>Received</Th><Th>Remaining</Th><Th>Unit Cost</Th><Th>Value</Th><Th>Supplier</Th><Th>Date</Th></tr></thead>
            <tbody className="divide-y">
              {lots.map(l => (
                <tr key={l.lot_id} className="hover:bg-gray-50">
                  <Td className="font-mono text-xs">{l.lot_id}</Td>
                  <Td className="font-medium">{products.find(p => p.product_id === l.product_id)?.name || l.product_id}</Td>
                  <Td>{l.quantity}</Td>
                  <Td className={l.remaining === 0 ? 'text-gray-300' : 'font-semibold'}>{l.remaining}</Td>
                  <Td>{cur}{l.unit_cost?.toFixed(2)}</Td>
                  <Td className="text-emerald-600 font-medium">{cur}{(l.remaining * l.unit_cost).toFixed(2)}</Td>
                  <Td className="text-gray-500">{l.supplier || '-'}</Td>
                  <Td className="text-xs text-gray-400">{l.created_at?.split('T')[0]}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {lots.length === 0 && <div className="p-8 text-center text-gray-400">No stock lots. Receive stock to create lot records.</div>}
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        {[
          { m: 'fifo', title: 'FIFO (First In, First Out)', desc: 'Oldest stock sold first. COGS reflects earlier (usually lower) prices. Higher reported profit in rising markets.' },
          { m: 'lifo', title: 'LIFO (Last In, First Out)', desc: 'Newest stock sold first. COGS reflects latest prices. Lower reported profit, can reduce tax liability.' },
          { m: 'average', title: 'Weighted Average', desc: 'Blended cost across all batches. Smooths out price fluctuations. Simplest method, widely used in Singapore.' },
        ].map(({ m, title, desc }) => (
          <div key={m} className={`p-4 rounded-xl border-2 ${costingMethod === m ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
            <h4 className="font-semibold text-sm mb-1">{title}</h4>
            <p className="text-xs text-gray-600">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
